import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Plus, Trash2, Save, RotateCcw, Share2, Mail, Download, Eye, ArrowLeft, Filter } from "lucide-react";
import { Field, lbSelect } from "./LogbookTaskCard";
import { SectionHead, Empty } from "./LogbookBoards";
import { orgContextLabel, reportPeriod, REPORT_TYPES, reportTypeLabel, reportShareText, whatsappUrl, mailUrl, fmtDateTime } from "./logbookUtils";

const errText = (e) => formatApiError(e?.response?.data?.detail) || e?.message || "Something went wrong";

const SUBTABS = [
  ["employees", "Employees"],
  ["departments", "Departments"],
  ["reports", "Reports"],
  ["history", "Reports history"],
];

/* Logbook Admin (Logbook admins only) — Employees, Departments, Reports and
   Reports history. Logbook's "Live status" (last seen + GPS location) is Time
   Tracking and is not part of the ERP. */
export default function LogbookAdmin({ ctx }) {
  const [sub, setSub] = useState("employees");
  if (!ctx.me.isAdmin) {
    return <Empty title="LOGBOOK ADMINS ONLY">Employees, departments and reports are managed by Logbook admins.</Empty>;
  }
  return (
    <div className="space-y-4" data-testid="lb-admin">
      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {SUBTABS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setSub(k)} data-testid={`lb-admin-${k}`}
            className={`px-3 py-2 text-sm -mb-px border-b-2 ${sub === k ? "border-blue-600 text-blue-700 font-medium" : "border-transparent text-slate-600 hover:text-slate-900"}`}>
            {label}
          </button>
        ))}
      </div>
      {sub === "employees" && <Employees ctx={ctx} />}
      {sub === "departments" && <Departments ctx={ctx} />}
      {sub === "reports" && <Reports ctx={ctx} />}
      {sub === "history" && <ReportHistory />}
    </div>
  );
}

/* ---------------- Employees ---------------- */
function Employees({ ctx }) {
  const { data, act } = ctx;
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [adding, setAdding] = useState(false);
  const [nu, setNu] = useState({ name: "", department: "", phone: "", pin: "" });

  const startEdit = (u) => {
    if (editId === u.id) { setEditId(null); return; }
    setEditId(u.id);
    setDraft({ name: u.name, pin: "", orgRole: u.orgRole || "employee", departmentId: u.departmentId || "", subDepartmentId: u.subDepartmentId || "" });
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}? This can't be undone.`)) return;
    try {
      await api.post("/hr/logbook/action/delete_user", { targetUserId: u.id });
      ctx.toastOk("Employee removed");
      setEditId(null);
      ctx.reload();
    } catch (e) {
      const d = e?.response?.data;
      if (d?.blockedByHistory && window.confirm(`${d.detail}\n\nPermanently delete "${u.name}" AND all of their tasks, attendance, and report history? This cannot be undone.`)) {
        if (await act("delete_user", { targetUserId: u.id, force: true }, "Employee and their history removed")) setEditId(null);
      } else if (!d?.blockedByHistory) {
        toast.error(errText(e));
      }
    }
  };

  return (
    <div className="space-y-3" data-testid="lb-employees">
      <SectionHead title="Everyone" count={`${data.users.length} total`} />
      <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
        {data.users.map((u) => (
          <div key={u.id} className="p-3">
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 flex items-center gap-1.5 flex-wrap">
                  {u.name}
                  {u.isAdmin && <span className="text-[10px] uppercase rounded bg-blue-50 text-blue-700 px-1.5 py-0.5">Admin</span>}
                  {u.orgRole === "dept_head" && <span className="text-[10px] uppercase rounded bg-violet-50 text-violet-700 px-1.5 py-0.5">Dept Head</span>}
                  {u.orgRole === "sub_head" && <span className="text-[10px] uppercase rounded bg-violet-50 text-violet-700 px-1.5 py-0.5">Sub Head</span>}
                </div>
                <div className="text-xs text-slate-500">
                  {orgContextLabel(u, data.departments, data.subDepartments)}{u.phone ? ` · ${u.phone}` : ""}{u.has_pin ? " · PIN set" : " · no PIN"}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => startEdit(u)} data-testid={`lb-emp-manage-${u.id}`}><Pencil className="w-3.5 h-3.5 mr-1" />{editId === u.id ? "Close" : "Manage"}</Button>
            </div>
            {editId === u.id && draft && (
              <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 space-y-3">
                <div className="flex gap-2">
                  <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Employee name" />
                  <Button size="sm" onClick={() => {
                    const newName = draft.name.trim();
                    if (!newName) return ctx.toastError("Name cannot be empty");
                    if (newName !== u.name) act("rename_user", { targetUserId: u.id, newName }, "Name updated");
                  }}>Save name</Button>
                </div>
                <div className="flex gap-2">
                  <Input value={draft.pin} inputMode="numeric" maxLength={4} onChange={(e) => setDraft({ ...draft, pin: e.target.value })} placeholder="New 4-digit PIN (blank = remove PIN)" />
                  <Button size="sm" onClick={async () => {
                    const pin = draft.pin.trim();
                    if (pin && !/^\d{4}$/.test(pin)) return ctx.toastError("PIN must be 4 digits");
                    if (await act("reset_pin", { targetUserId: u.id, newPin: pin }, "PIN reset")) setEditId(null);
                  }}>Reset PIN</Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Field label="Role">
                    <select className={lbSelect} value={draft.orgRole} onChange={(e) => setDraft({ ...draft, orgRole: e.target.value })}>
                      <option value="employee">Employee</option><option value="sub_head">Sub-department Head</option><option value="dept_head">Department Head</option>
                    </select>
                  </Field>
                  <Field label="Department">
                    <select className={lbSelect} value={draft.departmentId} onChange={(e) => setDraft({ ...draft, departmentId: e.target.value, subDepartmentId: "" })}>
                      <option value="">— none —</option>
                      {data.departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </Field>
                  {draft.orgRole !== "dept_head" && (
                    <Field label="Sub-department">
                      <select className={lbSelect} value={draft.subDepartmentId} disabled={!draft.departmentId} onChange={(e) => setDraft({ ...draft, subDepartmentId: e.target.value })}>
                        <option value="">— none —</option>
                        {data.subDepartments.filter((s) => s.departmentId === draft.departmentId).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </Field>
                  )}
                </div>
                <Button size="sm" onClick={async () => {
                  const { orgRole, departmentId, subDepartmentId } = draft;
                  if (orgRole === "dept_head" && !departmentId) return ctx.toastError("Pick a department for a department head");
                  if (orgRole === "sub_head" && !subDepartmentId) return ctx.toastError("Pick a sub-department for a sub-department head");
                  if (await act("update_user_org", { targetUserId: u.id, orgRole, departmentId: departmentId || null, subDepartmentId: subDepartmentId || null }, "Role updated")) setEditId(null);
                }}>Save role &amp; department</Button>
                <div className="flex gap-2 pt-1 border-t border-slate-200">
                  <Button size="sm" variant="outline" onClick={() => act("toggle_admin", { targetUserId: u.id, isAdmin: !u.isAdmin }, u.isAdmin ? "Admin removed" : "Now an admin")}>
                    {u.isAdmin ? "Remove admin" : "Make admin"}
                  </Button>
                  <Button size="sm" variant="outline" className="ml-auto text-red-600 border-red-200" onClick={() => remove(u)}><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        {adding ? (
          <div className="space-y-2">
            <Field label="Name"><Input value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} placeholder="Full name" data-testid="lb-new-emp-name" /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Field label="Department"><Input value={nu.department} onChange={(e) => setNu({ ...nu, department: e.target.value })} placeholder="e.g. Production" /></Field>
              <Field label="Phone"><Input value={nu.phone} onChange={(e) => setNu({ ...nu, phone: e.target.value })} placeholder="Optional" /></Field>
              <Field label="Initial PIN (optional)"><Input value={nu.pin} inputMode="numeric" maxLength={4} onChange={(e) => setNu({ ...nu, pin: e.target.value })} placeholder="4 digits" /></Field>
            </div>
            <p className="text-xs text-slate-500">Set their role, department and sub-department afterwards via Manage — this just gets them into the system.</p>
            <div className="flex gap-2">
              <Button size="sm" onClick={async () => {
                const name = nu.name.trim();
                if (!name) return ctx.toastError("Name required");
                if (nu.pin && !/^\d{4}$/.test(nu.pin.trim())) return ctx.toastError("PIN must be 4 digits");
                if (await act("add_user", { name, department: nu.department.trim() || null, phone: nu.phone.trim() || null, pin: nu.pin.trim() || null }, "Employee added")) {
                  setAdding(false); setNu({ name: "", department: "", phone: "", pin: "" });
                }
              }}>Add employee</Button>
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </div>
        ) : <Button size="sm" onClick={() => setAdding(true)} data-testid="lb-add-emp"><Plus className="w-3.5 h-3.5 mr-1" />Add employee</Button>}
      </div>
    </div>
  );
}

/* ---------------- Departments ---------------- */
function Departments({ ctx }) {
  const { data, act } = ctx;
  const [newDept, setNewDept] = useState(null);
  const [subFor, setSubFor] = useState(null);
  const [subName, setSubName] = useState("");
  return (
    <div className="space-y-3" data-testid="lb-departments">
      <SectionHead title="Structure" count={`${data.departments.length} total`} />
      <p className="text-sm text-slate-600">Department Heads can assign tasks to anyone in their department (sub-department heads or employees directly). Sub-department Heads can assign tasks to employees in their own sub-department. Set each person's role and department under Employees.</p>
      {data.departments.length === 0 && <Empty title="NONE YET">Add a department below to get started.</Empty>}
      {data.departments.map((d) => {
        const subs = data.subDepartments.filter((s) => s.departmentId === d.id);
        return (
          <div key={d.id} className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="flex-1 font-medium text-slate-900">{d.name}</span>
              <Button size="sm" variant="outline" onClick={() => { setSubFor(subFor === d.id ? null : d.id); setSubName(""); }}><Plus className="w-3.5 h-3.5 mr-1" />Sub-department</Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => {
                if (window.confirm("Delete this department? It must have no sub-departments and no employees assigned to it.")) act("delete_department", { departmentId: d.id }, "Department deleted");
              }}><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>
            </div>
            {subs.length ? subs.map((s) => (
              <div key={s.id} className="flex items-center gap-2 pl-3 border-l-2 border-slate-200">
                <span className="flex-1 text-sm text-slate-700">{s.name}</span>
                <Button size="sm" variant="ghost" className="text-red-600 h-7" onClick={() => {
                  if (window.confirm("Delete this sub-department? It must have no employees assigned to it.")) act("delete_sub_department", { subDepartmentId: s.id }, "Sub-department deleted");
                }}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            )) : <p className="text-xs text-slate-400">No sub-departments yet.</p>}
            {subFor === d.id && (
              <div className="flex gap-2">
                <Input value={subName} onChange={(e) => setSubName(e.target.value)} placeholder="Sub-department name" />
                <Button size="sm" onClick={async () => {
                  if (!subName.trim()) return ctx.toastError("Name required");
                  if (await act("add_sub_department", { name: subName.trim(), departmentId: d.id }, "Sub-department added")) setSubFor(null);
                }}>Add</Button>
              </div>
            )}
          </div>
        );
      })}
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        {newDept !== null ? (
          <div className="flex gap-2">
            <Input value={newDept} onChange={(e) => setNewDept(e.target.value)} placeholder="Department name, e.g. Production" />
            <Button size="sm" onClick={async () => {
              if (!newDept.trim()) return ctx.toastError("Name required");
              if (await act("add_department", { name: newDept.trim() }, "Department added")) setNewDept(null);
            }}>Add department</Button>
            <Button size="sm" variant="outline" onClick={() => setNewDept(null)}>Cancel</Button>
          </div>
        ) : <Button size="sm" onClick={() => setNewDept("")}><Plus className="w-3.5 h-3.5 mr-1" />Add department</Button>}
      </div>
    </div>
  );
}

/* ---------------- Reports ---------------- */
function ReportView({ data, onBack }) {
  const isDept = data.type === "department_kpi" || (data.rows?.[0] && "department" in data.rows[0]);
  const range = data.rangeStart || data.rangeEnd ? `${data.rangeStart || "…"} to ${data.rangeEnd || "…"}` : "All time";
  const rows = data.rows || [];
  const max = Math.max(1, ...rows.map((r) => r.avgEfficiency || 0));
  const { title, lines } = reportShareText(data);
  const exportCsv = () => {
    const cols = isDept
      ? [["department", "Department"], ["headcount", "Headcount"], ["total", "Total"], ["open", "Open"], ["overdue", "Overdue"], ["completed", "Completed"], ["avgEfficiency", "Efficiency %"]]
      : [["name", "Employee"], ["assigned", "Assigned"], ["completed", "Completed"], ["avgEfficiency", "Avg Efficiency %"]];
    const esc = (v) => `"${String(v === null || v === undefined ? "" : v).replace(/"/g, '""')}"`;
    const csv = [cols.map((c) => esc(c[1])).join(","), ...rows.map((r) => cols.map((c) => esc(r[c[0]])).join(","))].join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `logbook_${data.type || "report"}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-3" data-testid="lb-report-view">
      <div className="flex flex-wrap gap-2">
        {onBack && <Button size="sm" variant="outline" onClick={onBack}><ArrowLeft className="w-3.5 h-3.5 mr-1" />Back to reports history</Button>}
        <Button size="sm" variant="outline" asChild><a href={whatsappUrl(`*${title}*\nRange: ${range}\n\n${lines}`)} target="_blank" rel="noopener noreferrer"><Share2 className="w-3.5 h-3.5 mr-1" />Share via WhatsApp</a></Button>
        <Button size="sm" variant="outline" asChild><a href={mailUrl(`${title} — ${new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`, `${title}\nRange: ${range}\n\n${lines}`)}><Mail className="w-3.5 h-3.5 mr-1" />Send via email</a></Button>
        <Button size="sm" variant="outline" onClick={exportCsv}><Download className="w-3.5 h-3.5 mr-1" />Export CSV</Button>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between gap-2"><h3 className="font-semibold text-slate-900">{reportTypeLabel(data.type)}</h3><span className="font-mono text-xs text-slate-500">{range}</span></div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Avg efficiency % by {isDept ? "department" : "employee"}</div>
        <div className="space-y-1">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="w-32 truncate text-slate-700">{isDept ? r.department : r.name}</span>
              <div className="flex-1 h-3 bg-slate-100 rounded"><div className="h-3 rounded bg-blue-500" style={{ width: `${((r.avgEfficiency || 0) / max) * 100}%` }} /></div>
              <span className="w-10 text-right font-mono">{r.avgEfficiency === null || r.avgEfficiency === undefined ? "—" : `${r.avgEfficiency}%`}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            {isDept
              ? <tr><th className="text-left px-3 py-2">Department</th><th className="px-3 py-2">Headcount</th><th className="px-3 py-2">Total</th><th className="px-3 py-2">Open</th><th className="px-3 py-2">Overdue</th><th className="px-3 py-2">Completed</th><th className="px-3 py-2">Efficiency</th></tr>
              : <tr><th className="text-left px-3 py-2">Employee</th><th className="px-3 py-2">Assigned</th><th className="px-3 py-2">Completed</th><th className="px-3 py-2">Avg Efficiency</th></tr>}
          </thead>
          <tbody className="font-mono text-center">
            {rows.map((r, i) => isDept ? (
              <tr key={i} className="border-t border-slate-100"><td className="px-3 py-1.5 text-left font-sans font-medium">{r.department}</td><td>{r.headcount}</td><td>{r.total}</td><td>{r.open}</td><td style={{ color: r.overdue ? "#dc2626" : undefined }}>{r.overdue}</td><td>{r.completed}</td><td>{r.avgEfficiency === null ? "—" : `${r.avgEfficiency}%`}</td></tr>
            ) : (
              <tr key={i} className="border-t border-slate-100"><td className="px-3 py-1.5 text-left font-sans font-medium">{r.name}</td><td>{r.assigned}</td><td>{r.completed}</td><td>{r.avgEfficiency === null ? "—" : `${r.avgEfficiency}%`}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Reports({ ctx }) {
  const [f, setF] = useState({ type: "task_summary", start: "", end: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const period = (p) => { const [start, end] = reportPeriod(p); setF({ ...f, start, end }); };
  return (
    <div className="space-y-4" data-testid="lb-reports">
      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3 max-w-2xl">
        <h3 className="font-semibold text-slate-900">New report</h3>
        <Field label="Report type" className="max-w-xs">
          <select className={lbSelect} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
            {REPORT_TYPES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </Field>
        <div className="flex flex-wrap gap-2">
          {[["day", "Today"], ["week", "This week"], ["month", "This month"], ["custom", "Custom"]].map(([k, l]) => (
            <Button key={k} size="sm" variant="outline" onClick={() => period(k)}>{l}</Button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="From (optional)"><Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} /></Field>
          <Field label="To (optional)"><Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} /></Field>
        </div>
        <div className="flex gap-2">
          <Button size="sm" disabled={busy} data-testid="lb-report-save" onClick={async () => {
            setBusy(true);
            const res = await ctx.act("generate_report", { type: f.type, rangeStart: f.start || null, rangeEnd: f.end || null }, null, { noReload: true });
            setBusy(false);
            if (res) { setResult(res.data); ctx.toastOk("Report saved — find it any time under Reports history"); }
          }}><Save className="w-3.5 h-3.5 mr-1" />Save report</Button>
          <Button size="sm" variant="outline" onClick={() => { setF({ type: "task_summary", start: "", end: "" }); setResult(null); }}><RotateCcw className="w-3.5 h-3.5 mr-1" />Reset</Button>
        </div>
      </div>
      {result && <ReportView data={result} />}
    </div>
  );
}

function ReportHistory() {
  const [filter, setFilter] = useState({ start: "", end: "" });
  const [applied, setApplied] = useState({ start: "", end: "" });
  const [list, setList] = useState(null);
  const [view, setView] = useState(null);
  const load = useCallback(async () => {
    setList(null);
    try {
      const { data } = await api.get("/hr/logbook/reports", { params: { rangeStart: applied.start, rangeEnd: applied.end } });
      setList(data.reports || []);
    } catch (e) { toast.error(errText(e)); setList([]); }
  }, [applied]);
  useEffect(() => { load(); }, [load]);

  if (view) return <ReportView data={view} onBack={() => setView(null)} />;
  const open = async (id) => {
    try { const { data } = await api.get(`/hr/logbook/reports/${encodeURIComponent(id)}`); setView(data.data); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="space-y-3" data-testid="lb-report-history">
      <SectionHead title="Saved reports" count={list ? `${list.length} saved` : ""} />
      <div className="rounded-lg border border-slate-200 bg-white p-3 flex flex-wrap items-end gap-3">
        <Field label="From"><Input type="date" value={filter.start} onChange={(e) => setFilter({ ...filter, start: e.target.value })} /></Field>
        <Field label="To"><Input type="date" value={filter.end} onChange={(e) => setFilter({ ...filter, end: e.target.value })} /></Field>
        <Button size="sm" onClick={() => setApplied(filter)}><Filter className="w-3.5 h-3.5 mr-1" />Filter</Button>
        <Button size="sm" variant="outline" onClick={() => { setFilter({ start: "", end: "" }); setApplied({ start: "", end: "" }); }}>Clear</Button>
      </div>
      {list === null ? <div className="text-sm text-slate-500">Loading report history…</div>
        : list.length === 0 ? <Empty title="NONE YET">Save a report from Reports to see it here.</Empty> : (
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Range</th><th className="text-left px-3 py-2">By</th><th className="text-left px-3 py-2">When</th><th /></tr></thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-3 py-1.5">{reportTypeLabel(r.type)}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{r.rangeStart || r.rangeEnd ? `${r.rangeStart || "…"} to ${r.rangeEnd || "…"}` : "All time"}</td>
                    <td className="px-3 py-1.5">{r.generatedByName}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{fmtDateTime(r.createdAt)}</td>
                    <td className="px-3 py-1.5 text-right"><Button size="sm" variant="outline" className="h-7" onClick={() => open(r.id)}><Eye className="w-3.5 h-3.5 mr-1" />View</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
